import {
  useState,
  ForwardedRef,
  useCallback,
  ChangeEvent,
  useId,
  useRef,
} from 'react';
import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuGroup,
  ActionListItem,
  ActionListItemOption,
  ActionMenuOptionGroup,
} from '@allergan-data-labs/alle-elements-action-menu';
import {
  Box,
  Center,
  Divider,
  InputGroup,
  InputLeftElement,
  forwardRef,
  useMergeRefs,
  useOutsideClick,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  InputRightElement,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { Chip } from '@allergan-data-labs/alle-elements-chip';
import { ChevronDownIcon } from '@allergan-data-labs/alle-elements-icons/24/chevronDownIcon';
import { ChevronUpIcon } from '@allergan-data-labs/alle-elements-icons/24/chevronUpIcon';
import { CloseSolidCircleIcon as CloseIcon20 } from '@allergan-data-labs/alle-elements-icons/20/closeSolidCircleIcon';
import { CloseSolidCircleIcon as CloseIcon24 } from '@allergan-data-labs/alle-elements-icons/24/closeSolidCircleIcon';
import { IconButton } from '@allergan-data-labs/alle-elements-button';
import {
  useInputSelectState,
  InputSelectContextProps,
  InputSelectProvider,
  useInputSelectContext,
} from './input-select.hook';
import {
  getAdditionalTextFieldStyles,
  getFocusedActionListItemStyles,
  getOptionsListStyles,
  getListContainerStyles,
  getClearIconStyles,
  getCloseIconStyles,
  getElementStyles,
  getRightElementContentStyles,
} from './input-select.theme';

export type InputSelectVariant = 'list' | 'combo' | 'multiselect';

export interface InputSelectProps extends Omit<ChakraInputProps, 'onSelect'> {
  /** Placeholder text
   * @default Select
   */
  placeholder?: string;

  /** The color mode that the Input Select will display.
   * @default light
   */
  colorMode?: ColorMode;

  /** The size of the Input Select
   * @default md
   */
  size?: 'sm' | 'md';

  /**
   * The type variant. When set to `list`, the user is limited to selecting a single item from a list of items. When set
   * to `combo`, the user has the option to type into the input field to filter the list, then select a single item from
   * the filtered items. When set to `multiselect`, the user has the option to type into the input field to filter the
   * list, then select multiple items item from the filtered items.
   * @default list
   */
  variant?: InputSelectVariant;

  /** The list of items that are displayed as options for the user to select. Note, when the `variant` is set to `combo`
   * or `multiselect`, these items will be filtered by the text that the user types into the Input field  */
  items: string[];

  /** When the `variant` is set to `multiselect`, this represents the items that are preselected when the component is
   * first displayed
   */
  defaultSelected?: string[];

  /** Handler that gets triggered after the user selects an item in the list. Note: When the `variant` is set to `list`,
   * or `combo`, this will still provide an array, but containing a single item.
   */
  onSelect?: (items: string[]) => void;
}

const InputSelectImpl = forwardRef(InputSelectComponent) as (
  props: InputSelectProps & {
    ref?: ForwardedRef<HTMLDivElement>;
  }
) => JSX.Element;

function InputSelectComponent(
  {
    placeholder = 'Select',
    colorMode: propColorMode,
    size = 'md',
    variant = 'list',
    items,
    defaultSelected,
    onSelect,
    ...rest
  }: InputSelectProps,
  ref?: ForwardedRef<HTMLDivElement>
) {
  const optionListMenuId = `option-list-menu-${useId()}`;
  const { colorMode } = useColorMode(propColorMode);
  const [value, setValue] = useState<string>('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [removeFocusBorder, setRemoveFocusBorder] = useState(false);
  const [filter, setFilter] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);
  const [selected, setSelected] = useState<Array<string>>(
    defaultSelected || []
  );
  const { focusedIndex, setFocusedIndex } = useInputSelectContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef(null);

  const addToSelection = useCallback(
    (item: string) => {
      if (!selected.includes(item)) {
        setSelected((previous) => {
          const selectedOptions = [...previous, item];
          if (onSelect) {
            onSelect(selectedOptions);
          }
          return selectedOptions;
        });
      }
    },
    [onSelect, setSelected, selected]
  );

  const removeFromSelection = useCallback(
    (item: string) => {
      setSelected((previous) => {
        const selectedOptions = previous.filter((value: string) => {
          return value !== item;
        });
        if (onSelect) {
          onSelect(selectedOptions);
        }
        return selectedOptions;
      });
    },
    [onSelect, setSelected]
  );

  const clearSelected = useCallback(() => {
    setSelected((prev) => []);
  }, [setSelected]);

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setMenuVisible(false);
      setFocusedIndex(null);
    },
  });

  const ClearIcon =
    size === 'sm' ? (
      <CloseIcon20 sx={getCloseIconStyles(colorMode)} />
    ) : (
      <CloseIcon24 sx={getCloseIconStyles(colorMode)} />
    );

  const clearInput = () => {
    setFilter('');
    setValue('');
    setSelected((prev) => {
      return variant === 'combo' ? [] : prev;
    });
    setFilteredItems((prev) => [...items]);
    setFocusedIndex(null);
    setMenuVisible(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const selectOption = useCallback(
    (item: string, checked?: boolean) => {
      if (variant === 'list' || variant === 'combo') {
        clearSelected();
        addToSelection(item);
        setMenuVisible(false);
        setValue(item);
      } else if (variant === 'multiselect') {
        if (checked) {
          removeFromSelection(item);
        } else {
          addToSelection(item);
        }
      }
    },
    [addToSelection, clearSelected, removeFromSelection, variant]
  );

  //mergedItems is used when navigating the list via keyboard. Since we only keep a single
  //index for the selected item when the up or down arrows are called, this merged list
  //holds all visible items depending on the variant. For multiselect, that means selected
  //items on top in addition to filtered items below.
  const mergedItems = useCallback((): string[] => {
    return variant === 'multiselect'
      ? [
          ...selected.sort(),
          ...filteredItems.filter((item) => !selected.includes(item)),
        ]
      : filteredItems;
  }, [selected, filteredItems, variant]);

  const renderActionMenu = () => {
    const options = // if the filter yields no results
      filteredItems.length === 0
        ? [
            <ActionListItem
              key={`no-results`}
              value={`No results`}
              sx={{
                ...getOptionsListStyles(colorMode),
              }}
            >
              {`No results for "${value}"`}
            </ActionListItem>,
          ]
        : filteredItems
            .filter((item) =>
              variant === 'multiselect' ? !selected.includes(item) : true
            )
            .map((item, index) =>
              renderActionListItem(
                item,
                index + (variant === 'multiselect' ? selected.length : 0), //account for selected items on top of list
                index + (variant === 'multiselect' ? selected.length : 0) ===
                  focusedIndex,
                colorMode
              )
            );
    const listVariant = <ActionMenuGroup title="">{options}</ActionMenuGroup>;

    const multiselectVariant = (
      <>
        {selected.length > 0 && (
          //render the selected items first, at the top of the action menu
          <ActionMenuOptionGroup
            key={`ActionMenuOptionGroup - selected  ${selected.length}`}
            type="checkbox"
            value={selected}
            title=""
          >
            {selected
              .sort()
              .map((item, index) =>
                renderActionListItem(
                  item,
                  index,
                  index === focusedIndex,
                  colorMode
                )
              )}
          </ActionMenuOptionGroup>
        )}
        {options.length > 0 && (
          //then render the filtered items at the bottom of the action menu
          <ActionMenuOptionGroup
            key={`ActionMenuOptionGroup - filtered ${options.length}`}
            type="checkbox"
            title=""
            value={[]}
            defaultChecked={true}
            defaultValue={[]}
          >
            {options}
          </ActionMenuOptionGroup>
        )}
      </>
    );
    return variant === 'multiselect' ? multiselectVariant : listVariant;
  };

  const renderActionListItem = (
    item: string,
    index: number,
    isFocused: boolean,
    colorMode: ColorMode
  ) => {
    //just list the item in each row
    const listVariant = (
      <ActionListItem
        key={`${item}-${index}`}
        sx={{
          ...getOptionsListStyles(colorMode),
          ...(isFocused ? getFocusedActionListItemStyles(colorMode) : {}),
        }}
        onClick={(e) => {
          selectOption(item);
        }}
        role="menuitem"
      >
        {item}
      </ActionListItem>
    );

    //each item has a checkbox beside it for multiple selections
    const multiselectVariant = (
      <ActionListItemOption
        key={`${item}-${index}`}
        value={item}
        onClick={(e) => {
          selectOption(item, selected.includes(item));
        }}
        onKeyDown={onKeyDownHandler}
        sx={{
          ...getOptionsListStyles(colorMode),
          ...(isFocused ? getFocusedActionListItemStyles(colorMode) : {}),
        }}
        isChecked={true}
        role="menuitem"
      >
        {item}
      </ActionListItemOption>
    );
    if (variant === 'list' || variant === 'combo') {
      return listVariant;
    }

    return multiselectVariant;
  };

  const onClickHandler = (_: React.MouseEvent<HTMLInputElement>) => {
    setRemoveFocusBorder(true);
    setMenuVisible(true);
  };
  const onBlurHandler = (_: React.FocusEvent<HTMLInputElement>) => {
    setRemoveFocusBorder(false);
  };

  const onKeyDownHandler = useCallback(
    (
      e:
        | React.KeyboardEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLButtonElement>
    ) => {
      switch (e.code) {
        case 'ArrowDown': {
          e.preventDefault();
          setFocusedIndex((prev) => {
            if (prev === null || prev === mergedItems().length - 1) {
              return 0;
            }
            return prev + 1;
          });
          setMenuVisible(true);
          break;
        }
        case 'Space':
        case 'Enter': {
          if (focusedIndex === null) {
            setFocusedIndex(0);
            setMenuVisible(true);
            break;
          } else {
            e.preventDefault();
            selectOption(
              mergedItems()[focusedIndex],
              selected.includes(mergedItems()[focusedIndex])
            );
            if (variant !== 'multiselect') {
              setFocusedIndex(null);
            }
            requestAnimationFrame(() => {
              inputRef?.current?.focus();
            });
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setFocusedIndex((prev) => {
            if (prev === null || prev === 0) {
              return mergedItems().length - 1;
            }
            return prev - 1;
          });
          break;
        }
        case 'Tab': {
          setMenuVisible(false);
          setFocusedIndex(null);
          break;
        }
        default: {
          if (variant === 'list') {
            e.preventDefault();
            break;
          }
        }
      }
    },
    [
      focusedIndex,
      selected,
      variant,
      mergedItems,
      selectOption,
      setFocusedIndex,
      setMenuVisible,
    ]
  );

  const updateFilter = (filterValue: string) => {
    setFilter(filterValue);
    const filtered = items.filter((item) => {
      return item.toLowerCase().includes(filterValue.toLowerCase());
    });
    setFilteredItems(filtered);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // for combo and multiselect variants, as you type, the list is filtered
    if (variant !== 'list') {
      setMenuVisible(true);
      setValue(e.target.value);
      updateFilter(e.target.value);
      requestAnimationFrame(() => {
        e.target.focus();
      });
    }
  };

  const clearSelection = () => {
    clearSelected();
    if (onSelect) {
      onSelect([]);
    }
  };

  const LeftContent = () => {
    return (
      variant === 'multiselect' &&
      selected.length > 0 && (
        <Box>
          <Chip
            text={`${selected.length}`}
            size="sm"
            tabIndex={-1}
            colorScheme="brand"
            isDisabled={false}
            colorMode={colorMode}
            onClose={clearSelection}
            onClick={clearSelection}
            data-testid="input-selection-count"
            aria-valuenow={selected.length}
          />
        </Box>
      )
    );
  };
  interface RightContentProps {
    menuOpen: boolean;
  }
  const RightContent = ({ menuOpen }: RightContentProps) => {
    return (
      <Box sx={getRightElementContentStyles()}>
        {(filter.length > 0 || value.length > 0) && variant !== 'list' && (
          <>
            <IconButton
              shape="circle"
              variant="ghost"
              colorScheme="action"
              colorMode={colorMode}
              aria-label="Clear"
              className="screen-reader-focusable"
              sx={getClearIconStyles(colorMode)}
              onClick={clearInput}
              data-testid="clear-input-field"
              icon={ClearIcon}
              size={size}
            />
            <Center sx={{ height: '16px', padding: '4' }}>
              <Divider
                orientation="vertical"
                borderColor={`${colorMode}.Border/Neutral/Subtle 3`}
              />
            </Center>
          </>
        )}
        {menuOpen ? (
          <ChevronUpIcon
            color={`${colorMode}.Icon/Neutral/Default`}
            onClick={() => setMenuVisible(false)}
          />
        ) : (
          <ChevronDownIcon
            color={`${colorMode}.Icon/Neutral/Default`}
            onClick={() => setMenuVisible(true)}
          />
        )}
      </Box>
    );
  };
  return (
    <Box ref={containerRef}>
      <InputGroup size={size}>
        <InputLeftElement sx={getElementStyles('left')}>
          <LeftContent />
        </InputLeftElement>
        <ChakraInput
          ref={useMergeRefs(ref, inputRef)}
          colorScheme={colorMode}
          sx={getAdditionalTextFieldStyles(
            colorMode,
            size,
            removeFocusBorder,
            selected,
            filter.length > 0,
            variant
          )}
          placeholder={
            //no placeholder for multiselect since the chip is displayed instead
            variant === 'multiselect' && selected.length > 0
              ? undefined
              : placeholder
          }
          onClick={onClickHandler}
          onFocus={() => {
            updateFilter(variant === 'list' ? '' : value);
          }}
          onKeyDown={onKeyDownHandler}
          onInput={handleChange}
          onBlur={onBlurHandler}
          value={value}
          data-testid="input-select"
          {...rest}
        />
        <InputRightElement sx={getElementStyles('right')}>
          <RightContent menuOpen={menuVisible} />
        </InputRightElement>
      </InputGroup>
      <Box sx={getListContainerStyles(menuVisible)}>
        <ActionMenu
          colorMode={colorMode}
          isOpen={menuVisible}
          id={optionListMenuId}
          initialFocusRef={inputRef}
          matchWidth={true}
          closeOnSelect={variant !== 'multiselect'}
        >
          <ActionMenuContent data-testid="actionMenuContent">
            {renderActionMenu()}
          </ActionMenuContent>
        </ActionMenu>
      </Box>
    </Box>
  );
}

/**
 * Search is a navigation method that allows users to quickly find information across an app or website.
 * Users input a query into the search field and then see related results.
 */
export const InputSelect = forwardRef<InputSelectProps, 'input'>(
  (props, ref) => {
    const value: InputSelectContextProps = {
      ...useInputSelectState(),
    };
    return (
      <InputSelectProvider value={value}>
        <InputSelectImpl ref={ref} {...props} />
      </InputSelectProvider>
    );
  }
);

export default InputSelect;
